import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, CheckCircle2, Download, RefreshCw, Bell } from 'lucide-react';
import { motion } from 'framer-motion';
import { generateW4PDF, downloadPDF } from '@/components/utils/pdfGeneration';

export default function W4Form() {
  const [employee, setEmployee] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    w4_version: 'pre_2020',
    filing_status: 'single',
    dependents: 0,
    w4_allowances: 0,
    w4_step2_multiple_jobs: false,
    w4_step3_total: 0,
    w4_step4c_extra_withholding: 0,
    claim_exempt: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const toTitleCase = (value) => {
    return String(value || '')
      .replaceAll('_', ' ')
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const computeW4Defaults = (emp, family) => {
    const hasSpouse = family.some(m => (m.relationship || '').toLowerCase() === 'spouse');
    const dependentCount = family.filter(m => m.is_dependent && (m.relationship || '').toLowerCase() !== 'spouse').length;

    // Determine filing status from demographics and family
    let detectedFilingStatus = emp.filing_status || 'single';
    if (!emp.filing_status) {
      if (hasSpouse) {
        detectedFilingStatus = 'married';
      } else if (dependentCount > 0) {
        detectedFilingStatus = 'head_of_household';
      }
    }

    const dependentsToUse = emp.dependents || dependentCount;
    const allowancesToUse = emp.w4_allowances || dependentsToUse;
    const w4VersionToUse = emp.w4_version || 'pre_2020';
    const step3TotalToUse =
      emp.w4_step3_total !== null && emp.w4_step3_total !== undefined
        ? Number.parseFloat(emp.w4_step3_total)
        : dependentsToUse * 2000;

    return {
      hasSpouse,
      dependentCount,
      detectedFilingStatus,
      dependentsToUse,
      allowancesToUse,
      w4VersionToUse,
      step3TotalToUse
    };
  };

  const buildHrNotificationMessage = ({ employee: emp, updatedData, w4VersionLabel, filingStatusText }) => {
    const base = `${emp.first_name} ${emp.last_name} has updated their W-4 withholding information.`;
    const common = `Version: ${w4VersionLabel}. Filing Status: ${filingStatusText}, Dependents: ${updatedData.dependents}`;

    if ((updatedData.w4_version || 'pre_2020') === 'pre_2020') {
      return `${base} ${common}, Allowances: ${updatedData.w4_allowances}`;
    }

    const step2Text = updatedData.w4_step2_multiple_jobs ? 'Yes' : 'No';
    const step3Text = `$${Number(updatedData.w4_step3_total || 0).toFixed(2)}`;
    const extraText = `$${Number(updatedData.w4_step4c_extra_withholding || 0).toFixed(2)}`;
    const exemptText = updatedData.w4_claim_exempt ? 'Yes' : 'No';

    return `${base} ${common}, Step 2 (multiple jobs): ${step2Text}, Step 3 total: ${step3Text}, Extra withholding: ${extraText}, Exempt: ${exemptText}`;
  };

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      
      if (employees.length > 0) {
        const emp = employees[0];
        setEmployee(emp);
        
        // Load family members
        const family = await api.entities.FamilyMember.filter({ 
          employee_id: emp.id,
          is_active: true 
        });
        setFamilyMembers(family);

        const defaults = computeW4Defaults(emp, family);
        
        setFormData({
          w4_version: defaults.w4VersionToUse,
          filing_status: defaults.detectedFilingStatus,
          dependents: defaults.dependentsToUse,
          w4_allowances: defaults.allowancesToUse,
          w4_step2_multiple_jobs: !!emp.w4_step2_multiple_jobs,
          w4_step3_total: Number.isFinite(defaults.step3TotalToUse) ? defaults.step3TotalToUse : 0,
          w4_step4c_extra_withholding:
            emp.w4_step4c_extra_withholding !== null && emp.w4_step4c_extra_withholding !== undefined
              ? Number.parseFloat(emp.w4_step4c_extra_withholding)
              : 0,
          claim_exempt: !!emp.w4_claim_exempt
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!employee) {
      toast.error('No employee data found');
      return;
    }

    setSaving(true);
    try {
      const dependentsToSave = Number.parseInt(formData.dependents) || 0;
      const isOldW4 = (formData.w4_version || 'pre_2020') === 'pre_2020';

      // Update employee record with W-4 information
      const updatedData = {
        w4_version: formData.w4_version || 'pre_2020',
        filing_status: formData.filing_status,
        dependents: dependentsToSave,
        ...(isOldW4
          ? {
              w4_allowances: Number.parseInt(formData.w4_allowances) || 0,
              w4_step2_multiple_jobs: false,
              w4_step3_total: null,
              w4_step4c_extra_withholding: null,
              w4_claim_exempt: false
            }
          : {
              // Keep allowances populated for legacy tax-savings logic that still expects it.
              // (If missing, calculations already fall back to dependents.)
              w4_allowances: dependentsToSave,
              w4_step2_multiple_jobs: !!formData.w4_step2_multiple_jobs,
              w4_step3_total: Number.parseFloat(formData.w4_step3_total) || 0,
              w4_step4c_extra_withholding: Number.parseFloat(formData.w4_step4c_extra_withholding) || 0,
              w4_claim_exempt: !!formData.claim_exempt
            })
      };

      await api.entities.Employee.update(employee.id, updatedData);

      // Reload to ensure data is fresh
      await loadData();

      // Send notification to HR managers
      try {
        const users = await api.entities.User.filter({ employer_id: employee.employer_id });
        const hrUsers = users.filter(u => ['employer_hr', 'sub_admin'].includes((u.user_role || '').toLowerCase()));

        const filingStatusText = toTitleCase(updatedData.filing_status);
        
        const w4VersionLabel = (updatedData.w4_version || 'pre_2020') === 'post_2020' ? 'New (2020+)' : 'Old (allowances)';
        const message = buildHrNotificationMessage({ employee, updatedData, w4VersionLabel, filingStatusText });

        for (const hrUser of hrUsers) {
          await api.asServiceRole.entities.Notification.create({
            user_id: hrUser.id,
            title: 'Employee W-4 Updated',
            message,
            type: 'info',
            link_url: `/employees/${employee.id}`,
            is_read: false
          });
        }
      } catch (error_) {
        console.error('Failed to send HR notification:', error_);
        // Don't block the success flow if notification fails
      }

      // Show success modal
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error saving W-4 information:', err);
      toast.error(err.message || 'Failed to update W-4 information', {
        duration: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadW4 = async () => {
    try {
      const pdf = await generateW4PDF(employee);
      downloadPDF(pdf, `W4_${employee.first_name}_${employee.last_name}.pdf`);
      toast.success('W-4 form downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate W-4 PDF');
    }
  };

  const handleAutoFillFromFamily = async () => {
    if (!employee) {
      toast.error('No employee data found');
      return;
    }

    setLoading(true);
    try {
      // Reload family members from the database to get latest data
      const family = await api.entities.FamilyMember.filter({ 
        employee_id: employee.id,
        is_active: true 
      });
      setFamilyMembers(family);
      
      // Re-calculate from family members
      const hasSpouse = family.some(m => m.relationship.toLowerCase() === 'spouse');
      const dependentCount = family.filter(m => 
        m.is_dependent && 
        m.relationship.toLowerCase() !== 'spouse'
      ).length;
      
      let detectedFilingStatus = 'single';
      if (hasSpouse) {
        detectedFilingStatus = 'married';
      } else if (dependentCount > 0) {
        detectedFilingStatus = 'head_of_household';
      }
      
      setFormData({
        ...formData,
        filing_status: detectedFilingStatus,
        dependents: dependentCount,
        w4_allowances: dependentCount,
        w4_step3_total: dependentCount * 2000
      });
      
      toast.success(
        `Refreshed! Found ${hasSpouse ? '1 spouse' : 'no spouse'} and ${dependentCount} dependent(s)`,
        { duration: 4000 }
      );
    } catch (err) {
      console.error('Error refreshing from family profile:', err);
      toast.error('Failed to refresh from family profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">W-4 Tax Withholding</h1>
        <p className="text-slate-500 mt-1">
          Update your federal tax withholding information
        </p>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-blue-900">Important Information</p>
            <p className="text-sm text-blue-700 mt-1">
              Your W-4 determines how much federal income tax is withheld from your paycheck. 
              Updating this information affects your Section 125 tax savings calculations.
            </p>
          </div>
        </CardContent>
      </Card>

      {(formData.w4_version || 'pre_2020') === 'pre_2020' && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-start gap-3">
            <Bell className="w-5 h-5 text-amber-700 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-900">You are currently on the legacy (allowances-based) W-4</p>
              <p className="text-sm text-amber-800 mt-1">
                The IRS redesigned Form W-4 starting in 2020 and removed “withholding allowances.” If your employer/payroll uses the
                newer W-4, these legacy fields may not match what Payroll has on file and could impact withholding accuracy.
              </p>
              <p className="text-sm text-amber-800 mt-2">
                Please contact HR/Payroll to update your official W-4, then switch this page to “New (2020+)” to keep your records aligned.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {familyMembers.length > 0 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-emerald-900">Auto-detected from Your Family Profile</p>
                <div className="space-y-1 text-sm text-emerald-800 mt-2">
                  {familyMembers.some(m => m.relationship.toLowerCase() === 'spouse') && (
                    <p>✓ Spouse detected: {familyMembers.find(m => m.relationship.toLowerCase() === 'spouse')?.first_name}</p>
                  )}
                  {familyMembers.some(m => m.is_dependent && m.relationship.toLowerCase() !== 'spouse') && (
                    <p>✓ {familyMembers.filter(m => m.is_dependent && m.relationship.toLowerCase() !== 'spouse').length} dependent(s) detected</p>
                  )}
                  <p className="text-emerald-700 mt-2 text-xs">
                    💡 Values below have been pre-filled based on your family information. You can adjust them if needed.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Federal W-4 Information</CardTitle>
            {employee && (
              <div className="text-sm text-slate-600 mt-2 space-y-1">
                <p><strong>Name:</strong> {employee.first_name} {employee.last_name}</p>
                {employee.ssn_last_four && (
                  <p><strong>SSN (Last 4):</strong> ***-**-{employee.ssn_last_four}</p>
                )}
                {employee.address && (
                  <p><strong>Address:</strong> {employee.address}{employee.city && `, ${employee.city}`}{employee.state && `, ${employee.state}`} {employee.zip_code}</p>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* W-4 Version */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">W-4 Format</Label>
              <RadioGroup
                value={formData.w4_version}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    w4_version: value
                  })
                }
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="post_2020" id="w4_post_2020" />
                  <Label htmlFor="w4_post_2020">New W-4 (2020+)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pre_2020" id="w4_pre_2020" />
                  <Label htmlFor="w4_pre_2020">Old W-4 (allowances / legacy)</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Filing Status */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Filing Status</Label>
              <RadioGroup value={formData.filing_status} onValueChange={(value) => setFormData({ ...formData, filing_status: value })}>
                <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                  <RadioGroupItem value="single" id="single" />
                  <Label htmlFor="single" className="cursor-pointer flex-1">
                    <div>
                      <p className="font-medium">Single or Married filing separately</p>
                      <p className="text-sm text-slate-600">Choose this if you're unmarried or legally separated</p>
                    </div>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                  <RadioGroupItem value="married" id="married" />
                  <Label htmlFor="married" className="cursor-pointer flex-1">
                    <div>
                      <p className="font-medium">Married filing jointly</p>
                      <p className="text-sm text-slate-600">Choose this if you're married and filing together</p>
                    </div>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                  <RadioGroupItem value="head_of_household" id="hoh" />
                  <Label htmlFor="hoh" className="cursor-pointer flex-1">
                    <div>
                      <p className="font-medium">Head of Household</p>
                      <p className="text-sm text-slate-600">Unmarried and pay more than half the household costs</p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Dependents */}
            <div className="space-y-2">
              <Label htmlFor="dependents" className="text-base font-semibold">
                Number of Dependents
              </Label>
              <Input
                id="dependents"
                type="number"
                min="0"
                value={formData.dependents}
                onChange={(e) => setFormData({ ...formData, dependents: Number.parseInt(e.target.value) || 0 })}
                className="max-w-xs"
              />
              <p className="text-sm text-slate-600">
                Qualifying children and other dependents you claim on your tax return
                {familyMembers.some(m => m.is_dependent && m.relationship.toLowerCase() !== 'spouse') && (
                  <span className="text-emerald-600 font-medium">
                    {' '}(Detected: {familyMembers.filter(m => m.is_dependent && m.relationship.toLowerCase() !== 'spouse').length} from family profile)
                  </span>
                )}
              </p>
            </div>

            {(formData.w4_version || 'pre_2020') === 'pre_2020' ? (
              <>
                {/* W4 Allowances */}
                <div className="space-y-2">
                  <Label htmlFor="allowances" className="text-base font-semibold">
                    W-4 Withholding Allowances (Legacy)
                  </Label>
                  <Input
                    id="allowances"
                    type="number"
                    min="0"
                    value={formData.w4_allowances}
                    onChange={(e) => setFormData({ ...formData, w4_allowances: Number.parseInt(e.target.value) || 0 })}
                    className="max-w-xs"
                  />
                  <p className="text-sm text-slate-600">
                    This is the older allowances-based W-4. Most payroll systems use the redesigned 2020+ W-4.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Step 2 */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Step 2: Multiple Jobs or Spouse Works</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="step2"
                      type="checkbox"
                      checked={!!formData.w4_step2_multiple_jobs}
                      onChange={(e) => setFormData({ ...formData, w4_step2_multiple_jobs: e.target.checked })}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="step2" className="font-normal">
                      Check if there are only two jobs total (or spouse works)
                    </Label>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="space-y-2">
                  <Label htmlFor="step3total" className="text-base font-semibold">
                    Step 3: Total Dependent Credits (Annual)
                  </Label>
                  <Input
                    id="step3total"
                    type="number"
                    min="0"
                    value={formData.w4_step3_total}
                    onChange={(e) => setFormData({ ...formData, w4_step3_total: Number.parseFloat(e.target.value) || 0 })}
                    className="max-w-xs"
                  />
                  <p className="text-sm text-slate-600">
                    This is the total amount from W-4 Step 3 (e.g., children × $2,000). Defaulted from your dependents.
                  </p>
                </div>

                {/* Step 4c */}
                <div className="space-y-2">
                  <Label htmlFor="step4c" className="text-base font-semibold">
                    Step 4(c): Extra Withholding (Per Pay Period)
                  </Label>
                  <Input
                    id="step4c"
                    type="number"
                    min="0"
                    value={formData.w4_step4c_extra_withholding}
                    onChange={(e) => setFormData({ ...formData, w4_step4c_extra_withholding: Number.parseFloat(e.target.value) || 0 })}
                    className="max-w-xs"
                  />
                </div>

                {/* Exempt */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Exemption</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="exempt"
                      type="checkbox"
                      checked={!!formData.claim_exempt}
                      onChange={(e) => setFormData({ ...formData, claim_exempt: e.target.checked })}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="exempt" className="font-normal">
                      Claim exempt from withholding
                    </Label>
                  </div>
                </div>
              </>
            )}

            {/* Summary */}
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <p className="font-medium text-emerald-900">Tax Impact Summary</p>
                </div>
                <div className="space-y-2 text-sm text-emerald-800">
                  <p>• W-4 Format: <strong>{(formData.w4_version || 'pre_2020') === 'post_2020' ? 'New (2020+)' : 'Old (allowances)'}</strong></p>
                  <p>• Filing Status: <strong>{formData.filing_status.replace('_', ' ')}</strong></p>
                  <p>• Dependents: <strong>{formData.dependents}</strong></p>
                  {(formData.w4_version || 'pre_2020') === 'pre_2020' ? (
                    <>
                      <p>• W-4 Allowances: <strong>{formData.w4_allowances}</strong></p>
                      <p className="text-emerald-700 mt-3">
                        Tip: More allowances typically means less tax withheld (but may owe at year-end).
                      </p>
                    </>
                  ) : (
                    <>
                      <p>• Step 2 (multiple jobs): <strong>{formData.w4_step2_multiple_jobs ? 'Yes' : 'No'}</strong></p>
                      <p>• Step 3 total: <strong>${Number(formData.w4_step3_total || 0).toFixed(2)}</strong></p>
                      <p>• Extra withholding: <strong>${Number(formData.w4_step4c_extra_withholding || 0).toFixed(2)}</strong></p>
                      <p>• Exempt: <strong>{formData.claim_exempt ? 'Yes' : 'No'}</strong></p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </motion.div>

      {/* Actions */}
      <div className="flex justify-between items-center gap-3">
        <div>
          {familyMembers.length > 0 && (
            <Button 
              variant="outline"
              onClick={handleAutoFillFromFamily}
              disabled={saving || loading}
              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Refresh from Family Profile'}
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleDownloadW4}
            disabled={!employee}
          >
            <Download className="w-4 h-4 mr-2" />
            Download Pre-filled W-4 PDF
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving || !employee}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save W-4 Information'
            )}
          </Button>
        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-green-600">
              <CheckCircle2 className="w-8 h-8" />
              W-4 Information Saved Successfully!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Your Changes Have Been Saved</p>
                  <p className="text-sm text-green-700 mt-1">
                    Your W-4 withholding information has been updated and will be used for all tax calculations.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-blue-900">HR Team Notified</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Your HR team has been automatically notified of your W-4 updates and will review them accordingly.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-slate-900">Updated Information:</p>
                <div className="space-y-1 text-slate-700">
                  <p>• <strong>Filing Status:</strong> {(() => {
                    const status = formData.filing_status.replaceAll('_', ' ');
                    return status.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                  })()}</p>
                  <p>• <strong>Dependents:</strong> {formData.dependents}</p>
                  <p>• <strong>W-4 Allowances:</strong> {formData.w4_allowances}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => {
                handleDownloadW4();
                setShowSuccessModal(false);
              }}
              className="flex-1"
            >
              <Download className="w-4 h-4 mr-2" />
              Download W-4 PDF
            </Button>
            <Button
              onClick={() => setShowSuccessModal(false)}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disclaimer */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> This form is for informational purposes and tax calculation within our system. 
            You may need to submit an official IRS Form W-4 to your employer's payroll department separately.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}